import { isFiniteNumber } from "../../utils/numberFormat";
import { L_SHAPE, RECTANGULAR_SHAPE } from "./types/configurations";
import {
  CONNECTOR_FAMILY_CODE_FLAT_FLIP,
  CONNECTOR_FAMILY_CODE_GAP,
  CONNECTOR_FAMILY_CODE_KKA_AISI410,
  CONNECTOR_FAMILY_CODE_KKA_COLOR,
  CONNECTOR_FAMILY_CODE_KKT_A4_AISI316,
  CONNECTOR_FAMILY_CODE_KKT_COLOR,
  CONNECTOR_FAMILY_CODE_SNAP,
  CONNECTOR_FAMILY_CODE_TERRALOCK,
  CONNECTOR_FAMILY_CODE_TERRALOCK_A2,
  CONNECTOR_FAMILY_CODE_TERRALOCK_PP,
  CONNECTOR_FAMILY_CODE_TVM,
} from "./types/connectorFamilies";
import { FASTENING_TYPE_CODE_VISIBLE_FASTENING, type FasteningTypeForSettings } from "./types/fasteningTypes";
import { SECONDARY_ITEM_CODE_PROFID, SECONDARY_ITEM_CODE_SNAP } from "./types/secondaryItems";
import {
  SUBSTRUCTURE_MATERIAL_CODE_ALUMINIUM,
  type SubstructureMaterialForSettings,
} from "./types/substructureMaterials";
import type { SubstructureForSettings } from "./types/substructures";
import { SUPPORT_FAMILY_CODE_GRANULO, SUPPORT_FAMILY_CODE_NAG } from "./types/supportFamilies";
import type { DeckDesignerConfigurationFormValues } from "./useAutoSaveDeckDesignerConfiguration";

export type DeckDesignerResultInput = Pick<
  DeckDesignerConfigurationFormValues,
  | "liveLoad"
  | "deflection"
  | "shape"
  | "base"
  | "height"
  | "secondaryBase"
  | "secondaryHeight"
  | "totalHeight"
  | "hasHorizontalBoards"
  | "fasteningType"
  | "gap"
  | "connector"
  | "connectorSubstructureScrew"
  | "connectorBoardScrew"
  | "boardWidth"
  | "longDifferentBoardWidth"
  | "shortDifferentBoardWidth"
  | "boardThickness"
  | "substructureMaterialClass"
  | "substructure"
  | "joistsSpacing"
  | "secondaryItem"
  | "support"
  | "supportScrew"
>;

// What the joist count and the load-based support spacing depend on (see computeMinSupportSpacing).
export type SupportSpacingInput = Pick<
  DeckDesignerResultInput,
  | "liveLoad"
  | "deflection"
  | "shape"
  | "base"
  | "height"
  | "secondaryBase"
  | "secondaryHeight"
  | "hasHorizontalBoards"
  | "substructureMaterialClass"
  | "substructure"
  | "joistsSpacing"
  | "support"
>;

export type BoardQuantity = {
  total: number;
  long: number;
  short: number;
  // "regular" boards are cut to the standard boardWidth; "different" boards are the single
  // custom-width board (0 or 1) used to make a run fit exactly. regularLong + differentLong === long
  // (same for short), so consumers that need a transparent breakdown can show both.
  regularLong: number;
  differentLong: number;
  regularShort: number;
  differentShort: number;
};

export type BoardFit = {
  hasIssue: boolean;
  suggestedBoardWidth: number | null;
  suggestedLongDifferentBoardWidth: number | null;
  suggestedShortDifferentBoardWidth: number | null;
};

export type SubstructureQuantity = {
  total: number;
  long: number;
  short: number;
};

export type SupportQuantity = {
  total: number;
  long: number;
  short: number;
  maxSpacingCm: number;
  spacingBySupportLoad: number | null;
  spacingByDeflection: number | null;
  governingLimit: "load" | "deflection" | "fixed";
};

export type DeckDesignerQuantities = {
  supports: number | null;
  supportScrews: number | null;
  substructureLength: number | null;
  connectors: number | null;
  connectorSubstructureScrews: number | null;
  connectorBoardScrews: number | null;
  secondaryItems: number | null;
};

export type DeckDesignerResult = DeckDesignerResultInput & {
  boardQuantity: BoardQuantity | null;
  boardFit: BoardFit | null;
  substructureQuantity: SubstructureQuantity | null;
  substructureLengthQuantity: number | null;
  supportQuantity: SupportQuantity | null;
  deckArea: number | null;
  wasteCoefficient: number | null;
  intersectionsQuantity: number | null;
  connectorQuantity: number | null;
  connectorSubstructureScrewQuantity: number | null;
  connectorBoardScrewQuantity: number | null;
  secondaryItemQuantity: number | null;
  quantities: DeckDesignerQuantities;
};

type BoardFitRegion = {
  lengthMm: number;
  count: number;
};

type ConnectorQuantityRule = {
  intersectionMultiplier: number;
  joistMultiplier?: number;
};

type SecondaryItemQuantityRule = {
  considerIntersections: boolean;
  joistMultiplier: number | null;
};

type ConnectorScrewQuantityRule =
  | {
      connectorsMultiplier: number;
      intersectionMultiplier?: never;
    }
  | {
      connectorsMultiplier?: never;
      intersectionMultiplier: number;
    };

const ALUMINIUM_MODULUS_OF_ELASTICITY = 70;
// Shared with BoardFitWarning, which suggests deck dimensions under the same fit rules.
export const BOARD_FIT_TOLERANCE_MM = 0.01;
export const MAX_BOARD_FIT_COUNT = 2000;
const JOISTS_SPACING_MIN = 0.3;
const JOISTS_SPACING_MAX = 1;
// The values the form's selects offer, shared with the support spacing suggestions (see useSupportOptions).
export const LIVE_LOAD_OPTIONS = [{ value: 2 }, { value: 3 }, { value: 4 }, { value: 5 }] as const;
export const JOISTS_SPACING_OPTIONS = [
  { value: 0.3 },
  { value: 0.35 },
  { value: 0.4 },
  { value: 0.45 },
  { value: 0.5 },
  { value: 0.55 },
  { value: 0.6 },
  { value: 0.65 },
  { value: 0.7 },
  { value: 0.75 },
  { value: 0.8 },
  { value: 0.85 },
  { value: 0.9 },
  { value: 0.95 },
  { value: 1 },
] as const;
export const DEFLECTION_OPTIONS = [
  {
    label: "L/300",
    value: 300,
  },
  {
    label: "L/400",
    value: 400,
  },
] as const;
const TOTAL_HEIGHT_CM_TO_MM = 10;
const METERS_TO_CENTIMETERS = 100;
const NAG_SUPPORT_SPACING_CM = 50;
const MILLIMETERS_TO_METERS = 1000;
const SNAP_ITEM_WASTE_COEFFICIENT = 1.05;
const CONNECTOR_BOARD_SCREWS_PER_CONNECTOR = 2;

const CONNECTOR_QUANTITY_RULES: Partial<Record<string, ConnectorQuantityRule>> = {
  [CONNECTOR_FAMILY_CODE_TVM]: { intersectionMultiplier: 1 },
  [CONNECTOR_FAMILY_CODE_FLAT_FLIP]: { intersectionMultiplier: 1 },
  [CONNECTOR_FAMILY_CODE_GAP]: { intersectionMultiplier: 1 },
  [CONNECTOR_FAMILY_CODE_SNAP]: { intersectionMultiplier: 2, joistMultiplier: 2 },
  [CONNECTOR_FAMILY_CODE_TERRALOCK]: { intersectionMultiplier: 1 },
  [CONNECTOR_FAMILY_CODE_TERRALOCK_A2]: { intersectionMultiplier: 1 },
  [CONNECTOR_FAMILY_CODE_TERRALOCK_PP]: { intersectionMultiplier: 1 },
  [CONNECTOR_FAMILY_CODE_KKT_COLOR]: { intersectionMultiplier: 2 },
  [CONNECTOR_FAMILY_CODE_KKT_A4_AISI316]: { intersectionMultiplier: 2 },
  [CONNECTOR_FAMILY_CODE_KKA_COLOR]: { intersectionMultiplier: 1 },
  [CONNECTOR_FAMILY_CODE_KKA_AISI410]: { intersectionMultiplier: 1 },
};

const CONNECTOR_SUBSTRUCTURE_SCREW_QUANTITY_RULES: Partial<Record<string, ConnectorScrewQuantityRule>> = {
  [CONNECTOR_FAMILY_CODE_TVM]: { connectorsMultiplier: 1 },
  [CONNECTOR_FAMILY_CODE_FLAT_FLIP]: { connectorsMultiplier: 1 },
  [CONNECTOR_FAMILY_CODE_GAP]: { connectorsMultiplier: 2 },
  [CONNECTOR_FAMILY_CODE_SNAP]: { intersectionMultiplier: 1 },
  [CONNECTOR_FAMILY_CODE_TERRALOCK]: { connectorsMultiplier: 1 },
  [CONNECTOR_FAMILY_CODE_TERRALOCK_A2]: { connectorsMultiplier: 1 },
  [CONNECTOR_FAMILY_CODE_TERRALOCK_PP]: { connectorsMultiplier: 1 },
  [CONNECTOR_FAMILY_CODE_KKT_COLOR]: { connectorsMultiplier: 1 },
  [CONNECTOR_FAMILY_CODE_KKT_A4_AISI316]: { connectorsMultiplier: 1 },
  [CONNECTOR_FAMILY_CODE_KKA_COLOR]: { connectorsMultiplier: 1 },
  [CONNECTOR_FAMILY_CODE_KKA_AISI410]: { connectorsMultiplier: 1 },
};

const CONNECTOR_BOARD_SCREW_QUANTITY_RULES: Partial<Record<string, ConnectorScrewQuantityRule>> = {
  [CONNECTOR_FAMILY_CODE_TERRALOCK]: { connectorsMultiplier: CONNECTOR_BOARD_SCREWS_PER_CONNECTOR },
  [CONNECTOR_FAMILY_CODE_TERRALOCK_A2]: { connectorsMultiplier: CONNECTOR_BOARD_SCREWS_PER_CONNECTOR },
  [CONNECTOR_FAMILY_CODE_TERRALOCK_PP]: { connectorsMultiplier: CONNECTOR_BOARD_SCREWS_PER_CONNECTOR },
};

const SECONDARY_ITEM_QUANTITY_RULES: Record<string, SecondaryItemQuantityRule> = {
  [SECONDARY_ITEM_CODE_PROFID]: {
    considerIntersections: false,
    joistMultiplier: null,
  },
  [SECONDARY_ITEM_CODE_SNAP]: {
    considerIntersections: true,
    joistMultiplier: 2,
  },
};

export const isAluminiumSubstructureMaterial = (substructureMaterial: SubstructureMaterialForSettings): boolean =>
  substructureMaterial.code === SUBSTRUCTURE_MATERIAL_CODE_ALUMINIUM;

export const isVisibleFasteningType = (fasteningType: FasteningTypeForSettings): boolean =>
  fasteningType.code === FASTENING_TYPE_CODE_VISIBLE_FASTENING;

export const getSecondaryItemQuantityRule = (
  secondaryItemCode: string | null | undefined,
): SecondaryItemQuantityRule | undefined =>
  secondaryItemCode == null ? undefined : SECONDARY_ITEM_QUANTITY_RULES[secondaryItemCode];

const getBoardSpanFitDifference = (region: BoardFitRegion, boardWidth: number, gap: number): number =>
  region.lengthMm - (region.count * boardWidth + Math.max(region.count - 1, 0) * gap);

const doesBoardWidthFitRegion = (region: BoardFitRegion, boardWidth: number, gap: number): boolean => {
  const difference = getBoardSpanFitDifference(region, boardWidth, gap);

  return Math.abs(difference) <= BOARD_FIT_TOLERANCE_MM || (difference > 0 && difference < Math.max(region.count, 1));
};

const doesBoardWidthFitRegions = (regions: BoardFitRegion[], boardWidth: number, gap: number): boolean =>
  regions.every((region) => doesBoardWidthFitRegion(region, boardWidth, gap));

const isDifferentBoardWidthInRange = (differentBoardWidth: number, boardWidth: number): boolean =>
  differentBoardWidth >= boardWidth && differentBoardWidth <= boardWidth * 2;

const getBoardCountForLength = (lengthMm: number, boardWidth: number, gap: number): number | null => {
  const boardTotalSpace = boardWidth + gap;

  if (boardTotalSpace <= 0) {
    return null;
  }

  return Math.max(1, Math.round((lengthMm + gap) / boardTotalSpace));
};

const doesBoardWidthFitRegionLengths = (regions: BoardFitRegion[], boardWidth: number, gap: number): boolean =>
  regions.every((region) => {
    const count = getBoardCountForLength(region.lengthMm, boardWidth, gap);

    if (!count) {
      return false;
    }

    return doesBoardWidthFitRegion({ lengthMm: region.lengthMm, count }, boardWidth, gap);
  });

const getDifferentBoardCountForLength = (
  lengthMm: number,
  boardWidth: number,
  gap: number,
  differentBoardWidth: number,
): number | null => {
  if (!isDifferentBoardWidthInRange(differentBoardWidth, boardWidth)) {
    return null;
  }

  const boardTotalSpace = boardWidth + gap;

  if (boardTotalSpace <= 0) {
    return null;
  }

  const rawCount = (lengthMm - differentBoardWidth) / boardTotalSpace + 1;
  const count = Math.round(rawCount);

  if (count < 1 || count > MAX_BOARD_FIT_COUNT || Math.abs(rawCount - count) > BOARD_FIT_TOLERANCE_MM) {
    return null;
  }

  const usedLength = (count - 1) * boardTotalSpace + differentBoardWidth;

  return Math.abs(lengthMm - usedLength) <= BOARD_FIT_TOLERANCE_MM ? count : null;
};

const doesDifferentBoardWidthFitRegion = (
  region: BoardFitRegion,
  boardWidth: number,
  gap: number,
  differentBoardWidth: number,
): boolean =>
  doesBoardWidthFitRegion(region, boardWidth, gap) ||
  getDifferentBoardCountForLength(region.lengthMm, boardWidth, gap, differentBoardWidth) != null;

const doesDifferentBoardWidthsFitRegions = (
  regions: BoardFitRegion[],
  boardWidth: number,
  gap: number,
  longDifferentBoardWidth: number | null | undefined,
  shortDifferentBoardWidth: number | null | undefined,
): boolean =>
  regions.every((region, index) => {
    if (doesBoardWidthFitRegion(region, boardWidth, gap)) {
      return true;
    }

    const matchingDifferentBoardWidth = index === 0 ? longDifferentBoardWidth : shortDifferentBoardWidth;

    return (
      isFiniteNumber(matchingDifferentBoardWidth) &&
      doesDifferentBoardWidthFitRegion(region, boardWidth, gap, matchingDifferentBoardWidth)
    );
  });

const getBoardWidthForCount = (region: BoardFitRegion, count: number, gap: number): number | null => {
  const rawBoardWidth = (region.lengthMm - Math.max(count - 1, 0) * gap) / count;
  const boardWidth = Math.floor(rawBoardWidth);

  return boardWidth > 0 ? boardWidth : null;
};

const getSuggestedBoardWidth = (regions: BoardFitRegion[], gap: number, currentBoardWidth: number): number | null => {
  const regionsByMismatch = [...regions].sort(
    (firstRegion, secondRegion) =>
      Math.abs(getBoardSpanFitDifference(secondRegion, currentBoardWidth, gap)) -
      Math.abs(getBoardSpanFitDifference(firstRegion, currentBoardWidth, gap)),
  );

  for (const referenceRegion of regionsByMismatch) {
    const currentDifference = getBoardSpanFitDifference(referenceRegion, currentBoardWidth, gap);
    const firstCountToTry =
      currentDifference > BOARD_FIT_TOLERANCE_MM ? referenceRegion.count + 1 : referenceRegion.count;

    for (let count = firstCountToTry; count <= MAX_BOARD_FIT_COUNT; count += 1) {
      const boardWidth = getBoardWidthForCount(referenceRegion, count, gap);

      if (boardWidth == null || boardWidth >= currentBoardWidth) {
        continue;
      }

      if (doesBoardWidthFitRegionLengths(regions, boardWidth, gap)) {
        return boardWidth;
      }
    }
  }

  return null;
};

const getDifferentBoardWidthForCount = (
  region: BoardFitRegion,
  count: number,
  boardWidth: number,
  gap: number,
): number | null => {
  const rawDifferentBoardWidth = region.lengthMm - Math.max(count - 1, 0) * (boardWidth + gap);
  const differentBoardWidth = Math.round(rawDifferentBoardWidth);

  return Math.abs(rawDifferentBoardWidth - differentBoardWidth) <= BOARD_FIT_TOLERANCE_MM &&
    isDifferentBoardWidthInRange(differentBoardWidth, boardWidth)
    ? differentBoardWidth
    : null;
};

const getSuggestedDifferentBoardWidthForRegion = (
  region: BoardFitRegion,
  gap: number,
  currentBoardWidth: number,
): number | null => {
  // The closest count to the current one, then the closest width to the current one, then the narrowest.
  let best: { width: number; countDistance: number; widthDistance: number } | null = null;

  for (let count = 1; count <= MAX_BOARD_FIT_COUNT; count += 1) {
    const width = getDifferentBoardWidthForCount(region, count, currentBoardWidth, gap);

    if (width == null) {
      continue;
    }

    const countDistance = Math.abs(count - region.count);
    const widthDistance = Math.abs(width - currentBoardWidth);

    if (!best || (countDistance - best.countDistance || widthDistance - best.widthDistance || width - best.width) < 0) {
      best = { width, countDistance, widthDistance };
    }
  }

  return best?.width ?? null;
};

const getSuggestedDifferentBoardWidths = (
  regions: BoardFitRegion[],
  gap: number,
  currentBoardWidth: number,
): { longDifferentBoardWidth: number | null; shortDifferentBoardWidth: number | null } | null => {
  let longDifferentBoardWidth: number | null = null;
  let shortDifferentBoardWidth: number | null = null;

  for (const [index, region] of regions.entries()) {
    if (doesBoardWidthFitRegion(region, currentBoardWidth, gap)) {
      continue;
    }

    const suggestedWidth = getSuggestedDifferentBoardWidthForRegion(region, gap, currentBoardWidth);

    if (suggestedWidth == null) {
      return null;
    }

    if (index === 0) {
      longDifferentBoardWidth = suggestedWidth;
    } else {
      shortDifferentBoardWidth = suggestedWidth;
    }
  }

  return longDifferentBoardWidth == null && shortDifferentBoardWidth == null
    ? null
    : { longDifferentBoardWidth, shortDifferentBoardWidth };
};

type ResolvedBoardCount = {
  count: number;
  // true when `count` includes the single custom-width board, i.e. count - 1 boards are regular-width.
  usesDifferentWidth: boolean;
};

const getResolvedBoardCount = (
  lengthMm: number,
  normalCount: number,
  boardWidth: number,
  gap: number,
  differentBoardWidth: number | null,
): ResolvedBoardCount => {
  if (
    differentBoardWidth == null ||
    !isDifferentBoardWidthInRange(differentBoardWidth, boardWidth) ||
    doesBoardWidthFitRegion({ lengthMm, count: normalCount }, boardWidth, gap)
  ) {
    return { count: normalCount, usesDifferentWidth: false };
  }

  const differentCount = getDifferentBoardCountForLength(lengthMm, boardWidth, gap, differentBoardWidth);
  return differentCount != null
    ? { count: differentCount, usesDifferentWidth: true }
    : { count: normalCount, usesDifferentWidth: false };
};

type BoardRuns = {
  // The lengths (mm) boards are laid across: the uncut part of the deck and, for an L, its cutout side.
  longLengthMm: number;
  shortLengthMm: number;
  boardWidth: number;
  gap: number;
};

// null when the dimensions, the board width or the gap can't give a board count.
const getBoardRuns = (input: DeckDesignerResultInput): BoardRuns | null => {
  const isHorizontal = input.hasHorizontalBoards !== false;
  const longCountLength = isHorizontal ? input.height : input.base;
  const longCutoutLength =
    input.shape === RECTANGULAR_SHAPE ? 0 : isHorizontal ? input.secondaryHeight : input.secondaryBase;
  const shortCountLength =
    input.shape === RECTANGULAR_SHAPE ? 0 : isHorizontal ? input.secondaryHeight : input.secondaryBase;

  if (
    !isFiniteNumber(longCountLength) ||
    !isFiniteNumber(longCutoutLength) ||
    !isFiniteNumber(shortCountLength) ||
    !isFiniteNumber(input.boardWidth) ||
    !isFiniteNumber(input.gap) ||
    longCountLength <= 0 ||
    longCutoutLength < 0 ||
    longCutoutLength >= longCountLength ||
    shortCountLength < 0 ||
    input.boardWidth <= 0 ||
    input.gap < 0
  ) {
    return null;
  }

  return {
    longLengthMm: (longCountLength - longCutoutLength) * 1000,
    shortLengthMm: shortCountLength * 1000,
    boardWidth: input.boardWidth,
    gap: input.gap,
  };
};

const computeBoardQuantity = (input: DeckDesignerResultInput): BoardQuantity | null => {
  const boardRuns = getBoardRuns(input);

  if (!boardRuns) {
    return null;
  }

  const { longLengthMm, shortLengthMm, boardWidth, gap } = boardRuns;
  const boardTotalSpace = boardWidth + gap;
  const normalLong = Math.round((longLengthMm + gap) / boardTotalSpace);
  const normalShort = input.shape === RECTANGULAR_SHAPE ? 0 : Math.floor((shortLengthMm + gap) / boardTotalSpace);
  const longDifferentBoardWidth =
    isFiniteNumber(input.longDifferentBoardWidth) && input.longDifferentBoardWidth > 0
      ? input.longDifferentBoardWidth
      : null;
  const shortDifferentBoardWidth =
    isFiniteNumber(input.shortDifferentBoardWidth) && input.shortDifferentBoardWidth > 0
      ? input.shortDifferentBoardWidth
      : null;
  const longResolved = getResolvedBoardCount(longLengthMm, normalLong, boardWidth, gap, longDifferentBoardWidth);
  const shortResolved: ResolvedBoardCount =
    input.shape === RECTANGULAR_SHAPE
      ? { count: 0, usesDifferentWidth: false }
      : getResolvedBoardCount(shortLengthMm, normalShort, boardWidth, gap, shortDifferentBoardWidth);

  return {
    long: longResolved.count,
    short: shortResolved.count,
    total: longResolved.count + shortResolved.count,
    regularLong: longResolved.usesDifferentWidth ? longResolved.count - 1 : longResolved.count,
    differentLong: longResolved.usesDifferentWidth ? 1 : 0,
    regularShort: shortResolved.usesDifferentWidth ? shortResolved.count - 1 : shortResolved.count,
    differentShort: shortResolved.usesDifferentWidth ? 1 : 0,
  };
};

const computeBoardFit = (input: DeckDesignerResultInput, boardQuantity: BoardQuantity | null): BoardFit | null => {
  const boardRuns = getBoardRuns(input);

  if (!boardQuantity || !boardRuns) {
    return null;
  }

  const { longLengthMm, shortLengthMm, boardWidth, gap } = boardRuns;
  const regions: BoardFitRegion[] =
    input.shape === RECTANGULAR_SHAPE
      ? [{ lengthMm: longLengthMm, count: boardQuantity.total }]
      : [
          { lengthMm: longLengthMm, count: boardQuantity.long },
          { lengthMm: shortLengthMm, count: boardQuantity.short },
        ];
  const validRegions = regions.filter((region) => region.lengthMm > 0 && region.count > 0);

  if (!validRegions.length) {
    return null;
  }

  const doesRegularBoardWidthFit = doesBoardWidthFitRegions(validRegions, boardWidth, gap);
  const doesDifferentBoardWidthFit = doesDifferentBoardWidthsFitRegions(
    validRegions,
    boardWidth,
    gap,
    input.longDifferentBoardWidth,
    input.shortDifferentBoardWidth,
  );
  const hasIssue = !doesRegularBoardWidthFit && !doesDifferentBoardWidthFit;
  const suggestedDifferentBoardWidths = hasIssue
    ? getSuggestedDifferentBoardWidths(validRegions, gap, boardWidth)
    : null;

  return {
    hasIssue,
    suggestedBoardWidth: hasIssue ? getSuggestedBoardWidth(validRegions, gap, boardWidth) : null,
    suggestedLongDifferentBoardWidth: suggestedDifferentBoardWidths?.longDifferentBoardWidth ?? null,
    suggestedShortDifferentBoardWidth: suggestedDifferentBoardWidths?.shortDifferentBoardWidth ?? null,
  };
};

const computeSubstructureQuantity = (input: SupportSpacingInput): SubstructureQuantity | null => {
  const isHorizontal = input.hasHorizontalBoards !== false;
  const countLength = isHorizontal ? input.base : input.height;
  const secondaryCountLength =
    input.shape === RECTANGULAR_SHAPE ? 0 : isHorizontal ? input.secondaryBase : input.secondaryHeight;

  if (
    !isFiniteNumber(countLength) ||
    !isFiniteNumber(secondaryCountLength) ||
    !isFiniteNumber(input.joistsSpacing) ||
    input.joistsSpacing < JOISTS_SPACING_MIN ||
    input.joistsSpacing > JOISTS_SPACING_MAX
  ) {
    return null;
  }

  const total = Math.round(countLength / input.joistsSpacing + 1);
  const long = Math.round((countLength - secondaryCountLength) / input.joistsSpacing + 1);

  return {
    total,
    long,
    short: total - long,
  };
};

// The joist spacing the substructure actually ends up with, which the support calculation uses as
// each joist's tributary width. computeSubstructureQuantity rounds the joist count to the nearest
// whole number, so the real spacing can come out wider than the selected (nominal) one. Mirrors how
// createBottomViewLayout positions the joists: a rectangle spreads them evenly across the whole
// width; an L spreads the long ones across the uncut part, and the short ones across the cutout
// side starting one step past the last long joist. For an L this returns the wider of the two, so
// a single support spacing stays safe for both kinds. Falls back to the nominal spacing when a
// group is too small to have a spacing (a single joist).
const computeEffectiveJoistsSpacing = (
  input: SupportSpacingInput,
  substructureQuantity: SubstructureQuantity,
): number | null => {
  const isHorizontal = input.hasHorizontalBoards !== false;
  const countLength = isHorizontal ? input.base : input.height;
  const secondaryCountLength =
    input.shape === RECTANGULAR_SHAPE ? 0 : isHorizontal ? input.secondaryBase : input.secondaryHeight;

  if (!isFiniteNumber(countLength) || !isFiniteNumber(secondaryCountLength) || !isFiniteNumber(input.joistsSpacing)) {
    return null;
  }

  if (input.shape === RECTANGULAR_SHAPE) {
    return substructureQuantity.total > 1 ? countLength / (substructureQuantity.total - 1) : input.joistsSpacing;
  }

  const longSpacing =
    substructureQuantity.long > 1
      ? (countLength - secondaryCountLength) / (substructureQuantity.long - 1)
      : input.joistsSpacing;
  const shortSpacing = substructureQuantity.short > 0 ? secondaryCountLength / substructureQuantity.short : 0;

  return Math.max(longSpacing, shortSpacing);
};

// The length of a long joist (across the whole deck) and of a short one (beside an L's cutout; 0 for a
// rectangle). null when the dimensions it depends on aren't numbers.
const getJoistLengths = (input: SupportSpacingInput): { long: number | null; short: number | null } => {
  const isHorizontal = input.hasHorizontalBoards !== false;

  return {
    long: isHorizontal ? input.height : input.base,
    short:
      input.shape === RECTANGULAR_SHAPE
        ? 0
        : isHorizontal && isFiniteNumber(input.height) && isFiniteNumber(input.secondaryHeight)
          ? input.height - input.secondaryHeight
          : !isHorizontal && isFiniteNumber(input.base) && isFiniteNumber(input.secondaryBase)
            ? input.base - input.secondaryBase
            : null,
  };
};

const computeSubstructureLengthQuantity = (
  input: DeckDesignerResultInput,
  substructureQuantity: SubstructureQuantity | null,
): number | null => {
  if (
    !input.substructure ||
    !input.substructure.substructureMaterials.some(isAluminiumSubstructureMaterial) ||
    !substructureQuantity
  ) {
    return null;
  }

  const { long: longSubstructureLength, short: shortSubstructureLength } = getJoistLengths(input);

  if (
    !isFiniteNumber(longSubstructureLength) ||
    !isFiniteNumber(shortSubstructureLength) ||
    longSubstructureLength <= 0 ||
    shortSubstructureLength < 0
  ) {
    return null;
  }

  return substructureQuantity.long * longSubstructureLength + substructureQuantity.short * shortSubstructureLength;
};

type StackedSupportLayout = {
  substructureQuantity: SubstructureQuantity;
  longSubstructureLength: number;
  shortSubstructureLength: number;
  // The height (mm) the supports are stacked up to, under the deck's fixed elements.
  supportHeightMm: number;
  supportMinHeight: number;
};

// What NAG pads and GRANULO layers are stacked from: the joists they're laid along and the height left
// under the boards, connector, secondary item and joist. null when that can't be told, or nothing is left.
const getStackedSupportLayout = (
  input: DeckDesignerResultInput,
  substructureQuantity: SubstructureQuantity | null,
): StackedSupportLayout | null => {
  const { long: longSubstructureLength, short: shortSubstructureLength } = getJoistLengths(input);

  if (
    !substructureQuantity ||
    !isFiniteNumber(longSubstructureLength) ||
    !isFiniteNumber(shortSubstructureLength) ||
    !isFiniteNumber(input.totalHeight) ||
    !isFiniteNumber(input.support?.minHeight) ||
    !isFiniteNumber(input.boardThickness) ||
    !isFiniteNumber(input.substructure?.height) ||
    longSubstructureLength <= 0 ||
    shortSubstructureLength < 0 ||
    input.totalHeight <= 0 ||
    input.support.minHeight <= 0 ||
    input.boardThickness <= 0 ||
    input.substructure.height <= 0
  ) {
    return null;
  }

  const fixedDeckHeightMm =
    input.boardThickness +
    (input.connector?.thickness ?? 0) +
    (input.secondaryItem?.thickness ?? 0) +
    input.substructure.height;
  const supportHeightMm = input.totalHeight * TOTAL_HEIGHT_CM_TO_MM - fixedDeckHeightMm;

  if (supportHeightMm <= 0) {
    return null;
  }

  return {
    substructureQuantity,
    longSubstructureLength,
    shortSubstructureLength,
    supportHeightMm,
    supportMinHeight: input.support.minHeight,
  };
};

const computeNagSupportQuantity = (
  input: DeckDesignerResultInput,
  substructureQuantity: SubstructureQuantity | null,
): SupportQuantity | null => {
  const layout = getStackedSupportLayout(input, substructureQuantity);

  if (!layout) {
    return null;
  }

  const { longSubstructureLength, shortSubstructureLength, supportHeightMm, supportMinHeight } = layout;
  const nagPerSupportPoint = Math.ceil(supportHeightMm / supportMinHeight);
  const longSupportPoints = Math.floor((longSubstructureLength * METERS_TO_CENTIMETERS) / NAG_SUPPORT_SPACING_CM) + 1;
  const shortSupportPoints =
    layout.substructureQuantity.short > 0
      ? Math.floor((shortSubstructureLength * METERS_TO_CENTIMETERS) / NAG_SUPPORT_SPACING_CM) + 1
      : 0;
  const long = longSupportPoints * nagPerSupportPoint;
  const short = shortSupportPoints * nagPerSupportPoint;

  return {
    total: long * layout.substructureQuantity.long + short * layout.substructureQuantity.short,
    long,
    short,
    maxSpacingCm: NAG_SUPPORT_SPACING_CM,
    spacingBySupportLoad: null,
    spacingByDeflection: null,
    governingLimit: "fixed",
  };
};

// GRANULO stripe-type supports (e.g. GRANULO100/ROLL/MAT110) are long rolls/mats that cover
// several support points at once, unlike NAG-style point pads. GRANULOPAD has no stripeLength and
// behaves exactly like NAG (stacked pads at each support point), so it reuses that calculation.
const computeGranuloSupportQuantity = (
  input: DeckDesignerResultInput,
  substructureQuantity: SubstructureQuantity | null,
): SupportQuantity | null => {
  const stripeLength = input.support?.stripeLength;

  if (!stripeLength) {
    return computeNagSupportQuantity(input, substructureQuantity);
  }

  const layout = getStackedSupportLayout(input, substructureQuantity);

  if (!layout) {
    return null;
  }

  const { longSubstructureLength, shortSubstructureLength, supportHeightMm, supportMinHeight } = layout;
  // supportHeightMm is guaranteed (by the height-based support filtering) to be an exact multiple
  // of minHeight, so this stacks whole layers with no leftover gap.
  const layers = Math.ceil(supportHeightMm / supportMinHeight);
  const totalJoistLengthMeters =
    layout.substructureQuantity.long * longSubstructureLength +
    layout.substructureQuantity.short * shortSubstructureLength;
  const stripeLengthMeters = stripeLength / MILLIMETERS_TO_METERS;
  const total = Math.ceil((layers * totalJoistLengthMeters) / stripeLengthMeters);

  return {
    total,
    long: total,
    short: 0,
    maxSpacingCm: 0,
    spacingBySupportLoad: null,
    spacingByDeflection: null,
    governingLimit: "fixed",
  };
};

const computeSupportQuantity = (
  input: DeckDesignerResultInput,
  substructureQuantity: SubstructureQuantity | null,
): SupportQuantity | null => {
  if (input.support?.supportFamily.code === SUPPORT_FAMILY_CODE_NAG) {
    return computeNagSupportQuantity(input, substructureQuantity);
  }

  if (input.support?.supportFamily.code === SUPPORT_FAMILY_CODE_GRANULO) {
    return computeGranuloSupportQuantity(input, substructureQuantity);
  }

  return computeLoadSupportQuantity(input, substructureQuantity);
};

// In m^4: the section's own value, or the rectangle's (base x height) when it has none.
export const getSecondMomentOfArea = (substructure: SubstructureForSettings): number | null =>
  substructure.secondMomentOfArea ??
  (isFiniteNumber(substructure.base)
    ? ((substructure.base / 1000) * Math.pow(substructure.height / 1000, 3)) / 12
    : null);

export const isLoadSupportFamilyCode = (supportFamilyCode: string): boolean =>
  supportFamilyCode !== SUPPORT_FAMILY_CODE_NAG && supportFamilyCode !== SUPPORT_FAMILY_CODE_GRANULO;

// The families whose spacing comes from the live load and the joist deflection (JFA, SUP).
const computeLoadSupportQuantity = (
  input: SupportSpacingInput,
  substructureQuantity: SubstructureQuantity | null,
): SupportQuantity | null => {
  const { long: longSubstructureLength, short: shortSubstructureLength } = getJoistLengths(input);
  const secondMomentOfArea = input.substructure ? getSecondMomentOfArea(input.substructure) : null;
  const modulusOfElasticity = input.substructureMaterialClass
    ? (input.substructureMaterialClass.modulusOfElasticity ?? ALUMINIUM_MODULUS_OF_ELASTICITY)
    : null;
  const maxLiveLoad = input.support?.supportFamily.maxLiveLoad;

  if (
    !substructureQuantity ||
    !isFiniteNumber(longSubstructureLength) ||
    !isFiniteNumber(shortSubstructureLength) ||
    !isFiniteNumber(input.joistsSpacing) ||
    !isFiniteNumber(input.liveLoad) ||
    !isFiniteNumber(input.deflection) ||
    !isFiniteNumber(secondMomentOfArea) ||
    !isFiniteNumber(modulusOfElasticity) ||
    !isFiniteNumber(maxLiveLoad) ||
    longSubstructureLength <= 0 ||
    shortSubstructureLength < 0 ||
    input.joistsSpacing < JOISTS_SPACING_MIN ||
    input.joistsSpacing > JOISTS_SPACING_MAX ||
    input.liveLoad <= 0 ||
    input.deflection <= 0 ||
    secondMomentOfArea <= 0 ||
    modulusOfElasticity <= 0 ||
    maxLiveLoad <= 0
  ) {
    return null;
  }

  const joistsSpacing = computeEffectiveJoistsSpacing(input, substructureQuantity);

  if (!isFiniteNumber(joistsSpacing) || joistsSpacing <= 0) {
    return null;
  }

  const secondMomentOfAreaMm4 = secondMomentOfArea * 10 ** 12;
  const spacingBySupportLoad = maxLiveLoad / input.liveLoad / joistsSpacing;
  const spacingByDeflection =
    Math.cbrt(
      ((1 / input.deflection) * (384 / 5) * modulusOfElasticity * secondMomentOfAreaMm4) /
        ((input.liveLoad * joistsSpacing) / 1000),
    ) / 1000;
  const governingSpacing = Math.min(spacingBySupportLoad, spacingByDeflection);
  const maxSupportSpacingCm = Math.round((governingSpacing * 100) / 5) * 5;

  if (maxSupportSpacingCm <= 0) {
    return null;
  }

  const long = Math.ceil((longSubstructureLength * 100) / maxSupportSpacingCm + 1);
  const short =
    substructureQuantity.short > 0 ? Math.ceil((shortSubstructureLength * 100) / maxSupportSpacingCm + 1) : 0;

  return {
    total: long * substructureQuantity.long + short * substructureQuantity.short,
    long,
    short,
    maxSpacingCm: maxSupportSpacingCm,
    spacingBySupportLoad,
    spacingByDeflection,
    governingLimit: spacingBySupportLoad <= spacingByDeflection ? "load" : "deflection",
  };
};

// The spacing between supports along the joists as the bottom view draws them (joist length / gaps, see
// getSupportSpacingForKind), in meters: the smaller of the long and short joists'. Load-based families
// only (JFA, SUP), where it depends on the family alone, not on the support. null when it can't be
// computed.
export const computeMinSupportSpacing = (input: SupportSpacingInput): number | null => {
  if (!input.support || !isLoadSupportFamilyCode(input.support.supportFamily.code)) {
    return null;
  }

  const supportQuantity = computeLoadSupportQuantity(input, computeSubstructureQuantity(input));
  const { long: longSubstructureLength, short: shortSubstructureLength } = getJoistLengths(input);

  if (!supportQuantity || !isFiniteNumber(longSubstructureLength) || supportQuantity.long < 2) {
    return null;
  }

  const longSpacing = longSubstructureLength / (supportQuantity.long - 1);

  return supportQuantity.short > 1 && isFiniteNumber(shortSubstructureLength)
    ? Math.min(longSpacing, shortSubstructureLength / (supportQuantity.short - 1))
    : longSpacing;
};

const computeSupportBillQuantity = (
  input: DeckDesignerResultInput,
  supportQuantity: SupportQuantity | null,
): number | null => (input.support ? (supportQuantity?.total ?? null) : null);

const computeDeckArea = (input: DeckDesignerResultInput): number | null => {
  if (!isFiniteNumber(input.base) || !isFiniteNumber(input.height) || input.base <= 0 || input.height <= 0) {
    return null;
  }

  if (input.shape === RECTANGULAR_SHAPE) {
    return input.base * input.height;
  }

  if (input.shape !== L_SHAPE || !isFiniteNumber(input.secondaryBase) || !isFiniteNumber(input.secondaryHeight)) {
    return null;
  }

  const area = input.base * input.height - input.secondaryBase * input.secondaryHeight;

  return area > 0 ? area : null;
};

const computeWasteCoefficient = (input: DeckDesignerResultInput, deckArea: number | null): number | null => {
  if (input.shape === RECTANGULAR_SHAPE) {
    return 1.05;
  }

  if (input.shape !== L_SHAPE || !isFiniteNumber(deckArea)) {
    return null;
  }

  return deckArea <= 30 ? 1.15 : 1.1;
};

const computeIntersectionsQuantity = (
  input: DeckDesignerResultInput,
  boardQuantity: BoardQuantity | null,
  substructureQuantity: SubstructureQuantity | null,
): number | null => {
  if (!boardQuantity || !substructureQuantity || !input.fasteningType) {
    return null;
  }

  if (isVisibleFasteningType(input.fasteningType)) {
    return boardQuantity.short * substructureQuantity.long + substructureQuantity.total * boardQuantity.long;
  }

  return (boardQuantity.long - 1) * substructureQuantity.short + substructureQuantity.long * (boardQuantity.total - 1);
};

const computeConnectorQuantity = (
  input: DeckDesignerResultInput,
  intersectionsQuantity: number | null,
  substructureQuantity: SubstructureQuantity | null,
): number | null => {
  if (!input.connector || !substructureQuantity || !isFiniteNumber(intersectionsQuantity)) {
    return null;
  }

  const rule = CONNECTOR_QUANTITY_RULES[input.connector.connectorFamily.code] ?? { intersectionMultiplier: 1 };

  return intersectionsQuantity * rule.intersectionMultiplier + substructureQuantity.total * (rule.joistMultiplier ?? 0);
};

const computeConnectorScrewQuantity = (
  input: DeckDesignerResultInput,
  intersectionsQuantity: number | null,
  connectorQuantity: number | null,
  rules: Partial<Record<string, ConnectorScrewQuantityRule>>,
  defaultRule: ConnectorScrewQuantityRule = { connectorsMultiplier: 1 },
): number | null => {
  if (
    !input.connector ||
    !input.fasteningType ||
    isVisibleFasteningType(input.fasteningType) ||
    !isFiniteNumber(connectorQuantity) ||
    !isFiniteNumber(intersectionsQuantity)
  ) {
    return null;
  }

  const rule = rules[input.connector.connectorFamily.code] ?? defaultRule;

  if (rule.intersectionMultiplier != null) {
    return intersectionsQuantity * rule.intersectionMultiplier;
  }

  return connectorQuantity * rule.connectorsMultiplier;
};

const computeSecondaryItemQuantity = (
  input: DeckDesignerResultInput,
  intersectionsQuantity: number | null,
  substructureQuantity: SubstructureQuantity | null,
): number | null => {
  const rule = getSecondaryItemQuantityRule(input.secondaryItem?.item.code);

  if (
    !rule?.considerIntersections ||
    !isFiniteNumber(rule.joistMultiplier) ||
    !isFiniteNumber(intersectionsQuantity) ||
    !substructureQuantity
  ) {
    return null;
  }

  return intersectionsQuantity + substructureQuantity.total * rule.joistMultiplier;
};

// The only inputs computeBoardQuantity and computeBoardFit read.
const BOARD_STAGE_INPUT_FIELDS = [
  "shape",
  "base",
  "height",
  "secondaryBase",
  "secondaryHeight",
  "hasHorizontalBoards",
  "boardWidth",
  "gap",
  "longDifferentBoardWidth",
  "shortDifferentBoardWidth",
] as const satisfies readonly (keyof DeckDesignerResultInput)[];

const hasSameFieldValues = (first: object, second: object, fields: readonly string[]): boolean =>
  fields.every((field) =>
    Object.is((first as Record<string, unknown>)[field], (second as Record<string, unknown>)[field]),
  );

// previous when next holds the same values - the stages are flat objects of primitives - so it keeps
// its identity.
const keepPreviousIfEqual = <T extends object | null>(next: T, previous: T | null | undefined): T => {
  if (next === null || previous == null) {
    return next;
  }

  const fields = Object.keys(next);

  return fields.length === Object.keys(previous).length && hasSameFieldValues(next, previous, fields) ? previous : next;
};

// previous: the last result, whose stages (board quantity and fit, substructure and support quantity,
// quantities) are kept when they come out the same, so what depends on one only (memoized geometry, the
// drawing, the board fit warning) isn't recomputed when an unrelated field changes, e.g. a screw. The
// board stage isn't even recomputed when its own inputs are unchanged: its fit suggestions loop.
export const computeDeckDesignerResult = (
  input: DeckDesignerResultInput,
  previous?: DeckDesignerResult | null,
): DeckDesignerResult => {
  const isBoardStageUnchanged = !!previous && hasSameFieldValues(input, previous, BOARD_STAGE_INPUT_FIELDS);
  const boardQuantity = isBoardStageUnchanged
    ? previous.boardQuantity
    : keepPreviousIfEqual(computeBoardQuantity(input), previous?.boardQuantity);
  const boardFit = isBoardStageUnchanged
    ? previous.boardFit
    : keepPreviousIfEqual(computeBoardFit(input, boardQuantity), previous?.boardFit);
  const substructureQuantity = keepPreviousIfEqual(computeSubstructureQuantity(input), previous?.substructureQuantity);
  const substructureLengthQuantity = computeSubstructureLengthQuantity(input, substructureQuantity);
  const supportQuantity = keepPreviousIfEqual(
    computeSupportQuantity(input, substructureQuantity),
    previous?.supportQuantity,
  );
  const deckArea = computeDeckArea(input);
  const wasteCoefficient = computeWasteCoefficient(input, deckArea);
  const intersectionsQuantity = computeIntersectionsQuantity(input, boardQuantity, substructureQuantity);
  const connectorQuantity = computeConnectorQuantity(input, intersectionsQuantity, substructureQuantity);
  const connectorSubstructureScrewQuantity = input.connectorSubstructureScrew
    ? computeConnectorScrewQuantity(
        input,
        intersectionsQuantity,
        connectorQuantity,
        CONNECTOR_SUBSTRUCTURE_SCREW_QUANTITY_RULES,
      )
    : null;
  const connectorBoardScrewQuantity = input.connectorBoardScrew
    ? computeConnectorScrewQuantity(
        input,
        intersectionsQuantity,
        connectorQuantity,
        CONNECTOR_BOARD_SCREW_QUANTITY_RULES,
        { connectorsMultiplier: CONNECTOR_BOARD_SCREWS_PER_CONNECTOR },
      )
    : null;
  const secondaryItemQuantity = computeSecondaryItemQuantity(input, intersectionsQuantity, substructureQuantity);
  const supportBillQuantity = computeSupportBillQuantity(input, supportQuantity);
  const quantities = keepPreviousIfEqual<DeckDesignerQuantities>(
    {
      supports: supportBillQuantity,
      supportScrews: input.supportScrew && supportQuantity ? supportQuantity.total * 2 : null,
      substructureLength: input.substructure ? substructureLengthQuantity : null,
      connectors:
        input.connector && connectorQuantity != null && wasteCoefficient != null
          ? Math.round(connectorQuantity * wasteCoefficient)
          : null,
      connectorSubstructureScrews:
        input.connectorSubstructureScrew && connectorSubstructureScrewQuantity != null && wasteCoefficient != null
          ? Math.round(connectorSubstructureScrewQuantity * wasteCoefficient)
          : null,
      connectorBoardScrews:
        input.connectorBoardScrew && connectorBoardScrewQuantity != null && wasteCoefficient != null
          ? Math.round(connectorBoardScrewQuantity * wasteCoefficient)
          : null,
      secondaryItems:
        input.secondaryItem && secondaryItemQuantity != null
          ? Math.round(secondaryItemQuantity * SNAP_ITEM_WASTE_COEFFICIENT)
          : null,
    },
    previous?.quantities,
  );

  return {
    ...input,
    boardQuantity,
    boardFit,
    substructureQuantity,
    substructureLengthQuantity,
    supportQuantity,
    deckArea,
    wasteCoefficient,
    intersectionsQuantity,
    connectorQuantity,
    connectorSubstructureScrewQuantity,
    connectorBoardScrewQuantity,
    secondaryItemQuantity,
    quantities,
  };
};
